import { useSyncExternalStore } from 'react';
import type { AuthResponse, AuthUser, MeResponse } from '@parts/shared';
import { getJson, postJson } from './api';
import { loadSettings } from './settings';

/**
 * Вход в приложение: кто вошёл, его роль и имя.
 * После входа загружаются личные настройки (тема, ширины столбцов), затем показывается приложение.
 */
interface AuthState {
  /** Проверка входа завершена. */
  checked: boolean;
  user: AuthUser | null;
  allowRegistration: boolean;
  pendingUsers: number;
  title: string;
}

let state: AuthState = { checked: false, user: null, allowRegistration: true, pendingUsers: 0, title: '' };
const listeners = new Set<() => void>();

function set(next: Partial<AuthState>): void {
  state = { ...state, ...next };
  listeners.forEach((l) => l());
}

export function getAuth(): AuthState {
  return state;
}

export function useAuth(): AuthState {
  return useSyncExternalStore(
    (l) => {
      listeners.add(l);
      return () => listeners.delete(l);
    },
    getAuth
  );
}

/** Кто вошёл (при открытии страницы и для обновления счётчика заявок). */
export async function loadMe(): Promise<void> {
  try {
    const me = await getJson<MeResponse>('/api/auth/me');
    if (me.user && !state.user) await loadSettings();
    set({ checked: true, user: me.user, allowRegistration: me.allowRegistration, pendingUsers: me.pendingUsers ?? 0, title: me.title });
  } catch {
    set({ checked: true, user: null });
  }
}

export async function login(loginValue: string, password: string): Promise<AuthResponse> {
  const res = await postJson<AuthResponse>('/api/auth/login', { login: loginValue, password });
  if (res.ok && 'user' in res) {
    await loadSettings();
    await loadMe();
  }
  return res;
}

export async function register(email: string, name: string, password: string): Promise<AuthResponse> {
  return postJson<AuthResponse>('/api/auth/register', { email, name, password });
}

export async function logout(): Promise<void> {
  try {
    await postJson('/api/auth/logout');
  } finally {
    set({ user: null });
  }
}

/** Сервер ответил «войдите» (сессия истекла или пользователя отключили) — показать экран входа. */
export function onUnauthorized(): void {
  if (state.user) set({ user: null });
}
